/**
 * Unified sign-in surface.
 *
 * - iOS native (Path B2): Supabase OAuth authorize URL opened in
 *   ASWebAuthenticationSession via the OAuthWebSession plugin, then
 *   exchangeCodeForSession on the returned `code`. Audience matches
 *   because we control the client_id used in the OAuth request.
 * - Android native: Capgo SocialLogin plugin → ID token →
 *   supabase.auth.signInWithIdToken (unchanged).
 * - Web: delegates to the existing Lovable broker
 *   (lovable.auth.signInWithOAuth).
 *
 * Callers should treat all paths identically; the only branching
 * concern in the UI is whether to show the Apple button (gated on
 * isIOS() — see nativeAuth.ts).
 */
import type { User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import {
  isAndroid,
  isIOS,
  isNative,
  signInWithGoogleNative,
} from "@/lib/nativeAuth";

export interface SignInResult {
  /** True if the call triggered a browser/OS redirect (web Lovable broker only). */
  redirected?: boolean;
  error?: { message: string } | null;
}

const IOS_OAUTH_REDIRECT = "like2fit://auth-callback";

async function runIosOAuth(
  provider: "apple" | "google",
): Promise<SignInResult> {
  const { openInWebAuthSession } = await import("@/lib/oauthBrowser");
  const { data, error: urlError } = await supabase.auth.signInWithOAuth({
    provider,
    options: {
      redirectTo: IOS_OAUTH_REDIRECT,
      skipBrowserRedirect: true,
      scopes: provider === "apple" ? "email name" : undefined,
      queryParams:
        provider === "google" ? { prompt: "select_account" } : undefined,
    },
  });
  if (urlError || !data?.url) {
    return {
      error: { message: urlError?.message ?? `${provider} sign-in init failed` },
    };
  }

  let callbackUrl: string | null;
  try {
    callbackUrl = await openInWebAuthSession(data.url);
  } catch (err) {
    const message = err instanceof Error ? err.message : `${provider} sign-in failed`;
    return { error: { message } };
  }

  // User cancelled the sheet — quiet success (no error toast).
  if (!callbackUrl) return {};

  // Supabase client defaults to implicit flow (no flowType set), so the
  // callback URL carries tokens in the hash fragment, not a ?code= query
  // param. Parse the hash and hand the tokens to setSession.
  let access_token: string | null = null;
  let refresh_token: string | null = null;
  let errDesc: string | null = null;
  try {
    const url = new URL(callbackUrl);
    const hash = url.hash.startsWith("#") ? url.hash.slice(1) : url.hash;
    const params = new URLSearchParams(hash);
    access_token = params.get("access_token");
    refresh_token = params.get("refresh_token");
    errDesc = params.get("error_description") ?? params.get("error");
  } catch {
    /* falls through to missing-tokens error */
  }

  if (errDesc) {
    return { error: { message: errDesc } };
  }
  if (!access_token || !refresh_token) {
    return {
      error: { message: `${provider} sign-in: missing tokens in callback` },
    };
  }

  const { data: sessionData, error: setErr } = await supabase.auth.setSession({
    access_token,
    refresh_token,
  });
  if (setErr) {
    return { error: { message: setErr.message } };
  }

  if (provider === "apple") {
    const user = sessionData.session?.user;
    if (user) await applyAppleFirstSigninContract(user);
  }

  return {};
}

export async function signInWithGoogle(): Promise<SignInResult> {
  if (isIOS()) {
    return runIosOAuth("google");
  }

  if (isAndroid() && isNative()) {
    try {
      const { idToken } = await signInWithGoogleNative();
      const { error } = await supabase.auth.signInWithIdToken({
        provider: "google",
        token: idToken,
      });
      if (error) return { error: { message: error.message } };
      return {};
    } catch (err) {
      const message = err instanceof Error ? err.message : "Google sign-in failed";
      return { error: { message } };
    }
  }

  const result = await lovable.auth.signInWithOAuth("google", {
    redirect_uri: window.location.origin,
  });
  return {
    redirected: result.redirected,
    error: result.error ? { message: result.error.message ?? "Google sign-in failed" } : null,
  };
}

export async function signInWithApple(): Promise<SignInResult> {
  if (isIOS()) {
    return runIosOAuth("apple");
  }

  if (isAndroid()) {
    return { error: { message: "Apple Sign-In is not available on Android" } };
  }

  const result = await lovable.auth.signInWithOAuth("apple", {
    redirect_uri: window.location.origin,
  });
  return {
    redirected: result.redirected,
    error: result.error ? { message: result.error.message ?? "Apple sign-in failed" } : null,
  };
}

/**
 * Apple first-signin contract: capture email + display name the one and
 * only time they're present in the new session. Idempotent — gated on
 * the `apple_first_signin_complete` flag. Strict policy: never overwrite
 * an existing non-null value with Apple's payload.
 *
 * Under Path B2 the relevant fields arrive in:
 *   - user.email (Apple's relay-aware address)
 *   - user.user_metadata.full_name OR user.user_metadata.name
 */
async function applyAppleFirstSigninContract(user: User): Promise<void> {
  const userId = user.id;
  const { data: existing } = await supabase
    .from("profiles")
    .select("apple_first_signin_complete, display_name, email")
    .eq("id", userId)
    .maybeSingle();

  if (existing?.apple_first_signin_complete) return;

  const meta = (user.user_metadata ?? {}) as Record<string, unknown>;
  const rawFullName =
    typeof meta.full_name === "string" && meta.full_name.trim().length > 0
      ? meta.full_name.trim()
      : typeof meta.name === "string" && meta.name.trim().length > 0
        ? meta.name.trim()
        : null;

  const patch: {
    id: string;
    apple_first_signin_complete: boolean;
    display_name?: string;
    email?: string;
  } = {
    id: userId,
    apple_first_signin_complete: true,
  };
  if (!existing?.display_name && rawFullName) {
    patch.display_name = rawFullName;
  }
  if (!existing?.email && user.email) {
    patch.email = user.email;
  }

  await supabase.from("profiles").upsert(patch, { onConflict: "id" });
}

/** Wraps supabase.auth.signOut() and also clears native plugin sessions. */
export async function signOut(): Promise<void> {
  // Phase 4b: delete push tokens BEFORE auth.signOut() so the bearer
  // token is still valid for the RPC's RLS check. Fire-and-forget —
  // failure must not block signout.
  try {
    const { deleteMyPushTokens } = await import("@/lib/pushTokens");
    await deleteMyPushTokens();
  } catch (err) {
    console.error("[signOut] deleteMyPushTokens failed", err);
  }
  if (isNative()) {
    const { signOutNative } = await import("@/lib/nativeAuth");
    await signOutNative();
  }
  await supabase.auth.signOut();
}
